import type { Prayer } from '@/types/prayer';
import {
  APP_META_STORE,
  CONTENT_OWNER_META_KEY,
  PRAYERS_STORE,
  getDb,
} from '@/lib/db';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { getSupabase } from '@/lib/supabase';
import { cj1, completePrayerDocument } from '@/lib/content-lifecycle/contracts';

export type PrayerOutboundExpectation = {
  id: string;
  updatedAt: string;
  rawText: string;
  binding: 'content' | 'theme';
  snapshot: string;
};

export type OutboundOwnerBinding = {
  ownerId: string;
  authEpoch: number;
};

const MAX_AUTHORIZED_PRAYERS = 120;

/** Transient content identity, never a timestamp or a persisted/logged value. */
export function prayerContentSnapshot(prayer: Prayer): string {
  return cj1({
    document: completePrayerDocument(prayer),
    // Canonical wire dates normalize equivalent ISO spellings. Freshness also
    // retains the exact authored strings; it must not hide a byte change.
    dates: [prayer.createdAt, prayer.updatedAt, prayer.answeredAt ?? null,
      prayer.testimonyWrittenAt ?? null, prayer.progressHistory.map((entry) => entry.date)],
  });
}

export function prayerContentMatches(snapshot: string, prayer: Prayer): boolean {
  try { return typeof snapshot === 'string' && snapshot !== '' && snapshot === prayerContentSnapshot(prayer); }
  catch { return false; }
}

export interface PrayerThemeInput {
  id: string;
  updatedAt: string;
  createdAt: string;
  text: string;
  summary?: string;
  testimony?: string;
}

export function prayerThemeInput(prayer: Prayer): PrayerThemeInput {
  return { id: prayer.id, updatedAt: prayer.updatedAt, createdAt: prayer.createdAt,
    text: prayer.rawText, summary: prayer.gardenSummary, testimony: prayer.testimony ?? undefined };
}

function themeSnapshot(input: PrayerThemeInput): string {
  return cj1([input.id, input.updatedAt, input.createdAt, input.text,
    input.summary ?? null, input.testimony ?? null]);
}

export function themeOutboundExpectation(input: PrayerThemeInput): PrayerOutboundExpectation {
  return { id: input.id, updatedAt: input.updatedAt, rawText: input.text,
    binding: 'theme', snapshot: themeSnapshot(input) };
}

export function prayerOutboundExpectation(prayer: Prayer): PrayerOutboundExpectation {
  return {
    id: prayer.id, updatedAt: prayer.updatedAt, rawText: prayer.rawText,
    binding: 'content', snapshot: prayerContentSnapshot(prayer),
  };
}

function snapshotMatches(expected: PrayerOutboundExpectation, live: Prayer): boolean {
  try {
    if (typeof expected.snapshot !== 'string' || expected.snapshot === '') return false;
    if (expected.binding === 'content') return prayerContentMatches(expected.snapshot, live);
    return expected.binding === 'theme' && expected.snapshot === themeSnapshot(prayerThemeInput(live));
  } catch { return false; }
}

/** Capture the synchronous auth identity before the caller's first await. */
export function captureOutboundOwnerBinding(): OutboundOwnerBinding | null {
  const ownerId = getAuthUserId();
  return typeof ownerId === 'string' && ownerId !== ''
    ? { ownerId, authEpoch: getAuthEpoch() }
    : null;
}

export function outboundOwnerStillCurrent(binding: OutboundOwnerBinding): boolean {
  return getAuthEpoch() === binding.authEpoch && getAuthUserId() === binding.ownerId;
}

/** Resolve a token only when it still belongs to the entry-time owner. */
export async function ownerBoundJsonHeaders(
  binding: OutboundOwnerBinding,
): Promise<Record<string, string> | null> {
  if (!outboundOwnerStillCurrent(binding)) return null;
  const client = getSupabase();
  if (!client) return null;
  try {
    const { data } = await client.auth.getSession();
    if (
      !outboundOwnerStillCurrent(binding) ||
      data.session?.user.id !== binding.ownerId ||
      !data.session.access_token
    ) return null;
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${data.session.access_token}`,
    };
  } catch {
    return null;
  }
}

/** Match an outbound input to the exact current prayer revision. */
function prayerMatchesExpectation(
  expected: PrayerOutboundExpectation,
  live: Prayer | null | undefined,
): boolean {
  return Boolean(
    live &&
    live.id === expected.id &&
    live.updatedAt === expected.updatedAt &&
    live.rawText === expected.rawText &&
    snapshotMatches(expected, live),
  );
}

/**
 * Authorize the complete outbound prayer snapshot against one atomic IDB read.
 * The durable owner row and every contributing prayer revision are read in the
 * same transaction, then the synchronous auth epoch is checked again. Zustand
 * is intentionally not consulted because its mirror may lag an edit or owner
 * install that already committed to IndexedDB.
 */
export async function authorizePrayerOutbound(
  binding: OutboundOwnerBinding,
  expectations: readonly PrayerOutboundExpectation[],
): Promise<boolean> {
  if (
    !outboundOwnerStillCurrent(binding) ||
    expectations.length === 0 ||
    expectations.length > MAX_AUTHORIZED_PRAYERS
  ) return false;

  const unique = new Map<string, PrayerOutboundExpectation>();
  for (const expected of expectations) {
    if (!expected.id || !expected.updatedAt) return false;
    const prior = unique.get(expected.id);
    if (
      prior &&
      (prior.updatedAt !== expected.updatedAt ||
        prior.rawText !== expected.rawText ||
        prior.binding !== expected.binding || prior.snapshot !== expected.snapshot)
    ) return false;
    unique.set(expected.id, expected);
  }

  try {
    const database = await getDb();
    const tx = database.transaction([APP_META_STORE, PRAYERS_STORE], 'readonly');
    const ownerRequest = tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
    const expectedRows = [...unique.values()];
    const prayerRequests = expectedRows.map((expected) =>
      tx.objectStore(PRAYERS_STORE).get(expected.id),
    );
    const [ownerRow, prayers] = await Promise.all([
      ownerRequest,
      Promise.all(prayerRequests),
    ]);
    await tx.done;
    return Boolean(
      outboundOwnerStillCurrent(binding) &&
      ownerRow?.value === binding.ownerId &&
      expectedRows.every((expected, index) =>
        prayerMatchesExpectation(expected, prayers[index]),
      ),
    );
  } catch {
    return false;
  }
}
